import { For, Show, createMemo } from "solid-js";
import type { Component } from "solid-js";
import { useBookingListQuery } from "../service";
import { CustomerCell } from "./CustomerCell";
import { useShellStore } from "@ui/state/shell";
import { TableDate } from "@ui/components/TableDate";

const resolve = <T,>(input: T | (() => T)): T => (typeof input === "function" ? (input as () => T)() : input);

const formatCurrency = (amount: number | null, currency: string | null): string => {
  if (amount === null || Number.isNaN(amount)) {
    return "-";
  }
  if (!currency) {
    return amount.toLocaleString();
  }
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(amount);
  } catch (_error) {
    return `${currency} ${amount}`;
  }
};

const formatSchedule = (scheduledDate: string | null, label: string | null): string => {
  if (scheduledDate && label) {
    return `${scheduledDate} - ${label}`;
  }
  if (scheduledDate) {
    return scheduledDate;
  }
  if (label) {
    return label;
  }
  return "Schedule pending";
};

// formatDateTime removed - using TableDate component instead

export const BookingListView: Component = () => {
  const query = useBookingListQuery();
  const {
    state,
    actions: { selectBooking },
  } = useShellStore();

  const bookings = createMemo(() => resolve(query.data) ?? []);
  const isLoading = () => resolve(query.isLoading);
  const isFetching = () => resolve(query.isFetching);

  return (
    <section class="chat-list-view">
      <header class="chat-list-header">
        <div>
          <h1 class="main-placeholder-title">Bookings pipeline</h1>
          <p>Track upcoming cleanings, scheduling status, and payment confirmations.</p>
        </div>
        <div class="chat-list-actions">
          <button
            type="button"
            class="button-primary refresh-button"
            onClick={() => void query.refetch()}
            disabled={isFetching()}
            title="Refresh"
          >
            <i class={`fa-solid fa-rotate-right ${isFetching() ? "spinning" : ""}`}></i>
          </button>
        </div>
      </header>

      <Show when={!isLoading()} fallback={<div class="placeholder-card">Loading bookings...</div>}>
        <Show when={bookings().length > 0} fallback={<div class="placeholder-card">No bookings recorded yet.</div>}>
          <table class="chat-table">
            <thead>
              <tr>
                <th class="mobile-hide">Booking</th>
                <th class="mobile-hide">Customer</th>
                <th>Service</th>
                <th>Status</th>
                <th class="mobile-hide">Schedule</th>
                <th class="mobile-hide">Amount</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              <For each={bookings()}>
                {(booking) => (
                  <tr
                    class={state.selectedBookingId === booking.id ? "is-selected" : ""}
                    onClick={() => selectBooking(booking.id, booking.userId)}
                  >
                    <td class="chat-user mobile-hide">{booking.id}</td>
                    <td class="chat-preview mobile-hide">
                      <CustomerCell userId={booking.userId ?? null} />
                    </td>
                    <td class="booking-service-cell">
                      <div class="booking-service-desktop">
                        {booking.serviceId ?? "TBD"}
                      </div>
                      <div class="booking-service-mobile">
                        <div class="booking-service-row-1">
                          {booking.serviceId ?? "TBD"}, {formatCurrency(booking.amount, booking.currency)}
                        </div>
                        <div class="booking-service-row-2">
                          {formatSchedule(booking.schedule.scheduledDate, booking.schedule.timeSlotLabel)}
                        </div>
                      </div>
                    </td>
                    <td>
                      <span class={`booking-status booking-status--${booking.status}`}>{booking.status}</span>
                    </td>
                    <td class="chat-preview mobile-hide">{formatSchedule(booking.schedule.scheduledDate, booking.schedule.timeSlotLabel)}</td>
                    <td class="mobile-hide">{formatCurrency(booking.amount, booking.currency)}</td>
                    <td class="chat-time"><TableDate date={booking.createdAt} /></td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </Show>
      </Show>
    </section>
  );
};
